import { useMemo } from 'react';
import { NoShowRateByType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsHooks, ChartCTA, DemoChip, InfoTipPopover } from '@frontend/analytics-shared';
import { CategoryBarChartData, Chart } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { NSRTypeComparisonText } from '../components/nsr-type-comparison-text';
import { useFetchNSROILocationsNoShowRate } from '../hooks/use-fetch-nsr-locations-no-show-rate';
import { useNSRRoiShallowStore } from '../hooks/use-nsr-roi-store';
import { commonMultiAppearance } from './common-values';
import { CommonChartProps } from './types';

const labels = {
  confirmed: i18next.t('Confirmed Appointments', { ns: 'analytics' }),
  groupNameHeader: i18next.t('Location', { ns: 'analytics' }),
  unconfirmed: i18next.t('Unconfirmed Appointments', { ns: 'analytics' }),
};

const colors = {
  confirmed: theme.colors.success20,
  unconfirmed: theme.colors.rose30,
};

const strokeColors = {
  confirmed: theme.colors.success40,
  unconfirmed: theme.colors.rose50,
};

// The keys used here are based on how BE sends the data.
const dataKeys: Record<string, string> = {
  Unconfirmed: 'unconfirmed',
  Confirmed: 'confirmed',
};

export const NSRRoiLocationsComparisonConfirmationStatus = ({ isDemoAccount }: CommonChartProps) => {
  const { t } = useTranslation('analytics');
  const { filters, setFilters } = useNSRRoiShallowStore('filters', 'setFilters');
  const { locationNames } = AnalyticsHooks.useAnalyticsOrgLocations({
    module: 'ROI',
  });

  const { data, isLoading } = useFetchNSROILocationsNoShowRate({
    noShowRateType: NoShowRateByType.APPOINTMENTS_CONFIRMED,
    filters,
  });

  const chartData: CategoryBarChartData = useMemo(() => {
    return (
      data?.data?.locations?.reduce<CategoryBarChartData>((acc, { aggregates = [], locationId = '' }) => {
        // Create a temporary map for quick lookup of values from aggregates
        const aggregateValueMap = aggregates.reduce<Record<string, number>>((mapAcc, { name = '', value = 0 }) => {
          const chartKey = dataKeys[name];

          if (chartKey) {
            mapAcc[chartKey] = value;
          }

          return mapAcc;
        }, {});

        const orderedChartDataKeys = Object.values(dataKeys);

        // Build the location's data object ensuring the order of orderedChartDataKeys
        const locationData: Record<string, number> = {};
        orderedChartDataKeys.forEach((chartKey) => {
          locationData[chartKey] = aggregateValueMap[chartKey] || 0;
        });

        acc[locationId] = locationData;
        return acc;
      }, {}) || {}
    );
  }, [data?.data?.locations]);

  const { highlight, description } = useMemo(() => {
    const locationCount = Object.keys(chartData).length;

    if (!locationCount) {
      return {
        highlight: '',
        description: '',
      };
    }

    const { sumOfConfirmedAppts, sumOfUconfirmedAppts } = Object.values(chartData).reduce(
      (acc, { confirmed, unconfirmed }) => {
        return {
          sumOfConfirmedAppts: (acc.sumOfConfirmedAppts || 0) + Math.round(confirmed || 0),
          sumOfUconfirmedAppts: (acc.sumOfUconfirmedAppts || 0) + Math.round(unconfirmed || 0),
        };
      },
      {
        sumOfConfirmedAppts: 0,
        sumOfUconfirmedAppts: 0,
      }
    );

    const avgConfirmedAppts = sumOfConfirmedAppts / locationCount;
    const avgUnconfirmedAppts = sumOfUconfirmedAppts / locationCount;

    if (avgConfirmedAppts > avgUnconfirmedAppts) {
      return {
        highlight: labels.confirmed,
        description: t('Send additional reminders for confirmed appointments to reduce no-shows'),
      };
    } else if (avgUnconfirmedAppts > avgConfirmedAppts) {
      return {
        highlight: labels.unconfirmed,
        description: t('Send additional reminders for unconfirmed appointments to reduce no-shows'),
      };
    } else {
      return {
        highlight: t('Equal No-Show Rates'),
        description: t('Send additional reminders for confirmed and unconfirmed appointments to reduce no-shows'),
      };
    }
  }, [chartData, t]);

  return (
    <Chart
      colors={colors}
      isLoading={isLoading}
      labels={{ ...labels, ...locationNames }}
      strokeColors={strokeColors}
      style={{ marginBottom: 0 }}
    >
      <Chart.Header
        title={t('No-Show Rate by Confirmation Status')}
        infoTip={
          <InfoTipPopover>
            {t(
              'A detailed breakdown of no-show rates based on whether appointments were confirmed or unconfirmed across locations, helping practices understand how confirmation status impacts attendance and adjust their reminder strategies accordingly'
            )}
          </InfoTipPopover>
        }
        leftElement={isDemoAccount && <DemoChip />}
      />
      <Chart.CategoryBarChart
        appearance={{
          ...commonMultiAppearance(filters),
          customTooltipData: (groupId) => [
            {
              color: colors.confirmed,
              formattedValue: formatters.appendPercent(chartData[groupId]?.confirmed),
              id: 'confirmed',
              label: labels.confirmed,
              value: chartData[groupId]?.confirmed,
            },
            {
              color: colors.unconfirmed,
              formattedValue: formatters.appendPercent(chartData[groupId]?.unconfirmed),
              id: 'unconfirmed',
              label: labels.unconfirmed,
              value: chartData[groupId]?.unconfirmed,
            },
          ],
          formatters: formatters.appendPercent,
          maxValues: {
            unconfirmed: 100,
            confirmed: 100,
          },
          tooltipBottomContentRenderer: (data) => {
            return (
              <Text color='subdued' size='small' style={{ maxWidth: '230px' }}>
                <NSRTypeComparisonText
                  forTooltip
                  formerMetric={t('Unconfirmed appointments')}
                  formerValue={data.unconfirmed}
                  latterMetric={t('Confirmed appointments')}
                  latterValue={data.confirmed}
                />
              </Text>
            );
          },
        }}
        data={chartData}
        onClick={({ group }) => {
          setFilters({
            locationIds: [group],
          });
        }}
      />
      <Chart.Legends noop />
      <ChartCTA
        buttonLabel={t('Send Reminder')}
        ctaType='sendReminders'
        title={t('Overall Highest No-Show')}
        highlight={highlight}
        description={description}
      />
    </Chart>
  );
};
